// The platform console's operations cards: health alerts and the AI switch (OPS-01, PLAT-08), invoices awaiting
// payment (BILL-06) and the AI usage record (PLAT-07). Counts, totals, metering and org names only.
import { type FormEvent, useMemo, useState } from "react";
import { useSession } from "../app/session";
import { Card, Empty, ErrorNote, Field, Loading, Modal, useAction, useLoad } from "../app/ui";
import { costSummary, periodRange, usageCsv, type UsagePeriod } from "../lib/aiUsage";
import { fmtDateTime, fmtDuration, fmtNumber, fmtRupees } from "../lib/format";
import { INTEGRATIONS, USE_CASES } from "../lib/integrations";
import type { PlatformInvoice } from "../lib/types";

const TZ = "Asia/Kolkata";

const aiModeLabel: Record<string, string> = { live: "On", paused: "Paused", fake: "Test mode" };

export function HealthCard() {
  const { api } = useSession();
  const { data, error, loading, reload } = useLoad(() => api.platformHealth(), [api]);
  const [switching, setSwitching] = useState<"live" | "paused" | null>(null);
  return (
    <Card
      title="Health"
      actions={data && (
        <button
          className={`small${data.ai_mode === "live" ? "" : " primary"}`}
          onClick={() => setSwitching(data.ai_mode === "live" ? "paused" : "live")}
        >
          {data.ai_mode === "live" ? "Pause AI" : "Turn AI on"}
        </button>
      )}
    >
      <ErrorNote error={error} />
      {loading && !data && <Loading />}
      {data && (
        <>
          <div className="kv" style={{ gridTemplateColumns: "repeat(5, minmax(0, 1fr))", marginBottom: 12 }}>
            <div>
              <span>AI</span>
              <strong className={data.ai_mode === "live" ? "" : "overdue"}>
                {aiModeLabel[data.ai_mode] ?? data.ai_mode}
              </strong>
            </div>
            <div>
              <span>Queued</span>
              <strong>{fmtNumber(data.queued)}</strong>
            </div>
            <div>
              <span>Held</span>
              <strong>{fmtNumber(data.held)}</strong>
            </div>
            <div>
              <span>Done, 24 h</span>
              <strong>{fmtNumber(data.done_24h)}</strong>
            </div>
            <div>
              <span>Failed, 24 h</span>
              <strong>{fmtNumber(data.failed_24h)}</strong>
            </div>
          </div>
          {data.ai_mode !== "live" && (
            <p className="faint">
              Recordings are saved and wait; nothing is transcribed or analysed until AI is on again. Turning it on
              queues every waiting recording.
            </p>
          )}
          {data.alerts.length === 0 && <p className="muted">No open alerts.</p>}
          <div className="list">
            {data.alerts.map((a) => (
              <div className="list-item row between" key={`${a.key}-${a.org ?? ""}`}>
                <span>
                  <span className={`badge ${a.severity === "critical" ? "danger" : "warm"}`}>{a.severity}</span>{" "}
                  {a.message}
                </span>
                <span className="faint">since {fmtDateTime(a.first_seen, TZ)}</span>
              </div>
            ))}
          </div>
        </>
      )}
      {switching && (
        <ReasonDialog
          title={switching === "paused" ? "Pause all AI processing" : "Turn AI processing on"}
          label="Reason, for the audit log"
          action={switching === "paused" ? "Pause AI" : "Turn AI on"}
          onClose={() => setSwitching(null)}
          onSubmit={(text) => api.platformSetAiMode(switching, text)}
          done={async () => {
            setSwitching(null);
            await reload();
          }}
        />
      )}
    </Card>
  );
}

export function InvoicesCard() {
  const { api } = useSession();
  const { data, error, loading, reload } = useLoad(() => api.platformInvoices("issued"), [api]);
  const [paying, setPaying] = useState<PlatformInvoice | null>(null);
  const [voiding, setVoiding] = useState<PlatformInvoice | null>(null);
  return (
    <Card title="Invoices awaiting payment" flush>
      <ErrorNote error={error} />
      {loading && !data && (
        <div style={{ padding: 16 }}>
          <Loading />
        </div>
      )}
      {data && data.length === 0 && <Empty>Nothing to collect.</Empty>}
      {data && data.length > 0 && (
        <div className="table-wrap">
          <table>
            <tbody>
              {data.map((i) => (
                <tr key={i.id}>
                  <td>
                    <div className="primary">{i.org_name}</div>
                    <div className="faint">{i.number} · {i.kind === "topup" ? "minute pack" : "plan"}</div>
                  </td>
                  <td className="num nowrap">{fmtRupees(i.total_paise)}</td>
                  <td className={`nowrap ${new Date(i.due_at) < new Date() ? "overdue" : ""}`}>
                    Due {fmtDateTime(i.due_at, TZ)}
                  </td>
                  <td>
                    <div className="row" style={{ justifyContent: "flex-end" }}>
                      <button className="small primary" onClick={() => setPaying(i)}>Record payment</button>
                      <button className="small" onClick={() => setVoiding(i)}>Cancel</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {paying && (
        <ReasonDialog
          title={`Payment for ${paying.number}`}
          label="Payment reference (UTR or UPI id)"
          action="Record payment"
          onClose={() => setPaying(null)}
          onSubmit={(text) => api.platformMarkPaid(paying.id, text)}
          done={async () => {
            setPaying(null);
            await reload();
          }}
        />
      )}
      {voiding && (
        <ReasonDialog
          title={`Cancel ${voiding.number}`}
          label="Reason"
          action="Cancel invoice"
          onClose={() => setVoiding(null)}
          onSubmit={(text) => api.platformVoidInvoice(voiding.id, text)}
          done={async () => {
            setVoiding(null);
            await reload();
          }}
        />
      )}
    </Card>
  );
}

function ReasonDialog(props: {
  title: string;
  label: string;
  action: string;
  onClose: () => void;
  onSubmit: (text: string) => Promise<void>;
  done: () => Promise<void>;
}) {
  const [text, setText] = useState("");
  const { busy, run } = useAction();
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (await run(() => props.onSubmit(text.trim()), "Saved")) await props.done();
  };
  return (
    <Modal title={props.title} onClose={props.onClose}>
      <form onSubmit={submit}>
        <Field label={props.label}>
          <input required autoFocus value={text} onChange={(e) => setText(e.target.value)} />
        </Field>
        <div className="actions">
          <button type="button" onClick={props.onClose}>Back</button>
          <button className="primary" disabled={busy || !text.trim()}>{props.action}</button>
        </div>
      </form>
    </Modal>
  );
}

const periodLabel: Record<UsagePeriod, string> = {
  today: "Today",
  "7d": "Last 7 days",
  month: "This month",
  last_month: "Last month",
};

const usd = (v: number | null | undefined, digits = 4) => (v == null ? "–" : `$${v.toFixed(digits)}`);

/**
 * What AI cost (PLAT-07): Gemini's token counts per model, company and call, at its published prices, with an
 * estimate where it reported no transcription output. Calls are metering only.
 */
export function AiUsageCard() {
  const { api } = useSession();
  const [period, setPeriod] = useState<UsagePeriod>("month");
  const [rupeesPerDollar, setRupeesPerDollar] = useState(88);
  const [showCalls, setShowCalls] = useState(25);
  const range = useMemo(() => periodRange(period), [period]);
  const { data, error, loading } = useLoad(() => api.platformAiUsage(range.from, range.to, 5000), [
    api,
    range.from,
    range.to,
  ]);
  const summary = data ? costSummary(data.totals) : null;
  const inr = (v: number | null | undefined) =>
    v == null ? "–" : `₹${(v * rupeesPerDollar).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
  const download = () => {
    if (!data) return;
    const url = URL.createObjectURL(new Blob([usageCsv(data.calls)], { type: "text/csv" }));
    const a = Object.assign(document.createElement("a"), { href: url, download: `ai-usage-${period}.csv` });
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Card
      title="AI usage and cost"
      actions={
        <span className="row">
          <select value={period} aria-label="Period" onChange={(e) => setPeriod(e.target.value as UsagePeriod)}>
            {(Object.keys(periodLabel) as UsagePeriod[]).map((p) => <option key={p} value={p}>{periodLabel[p]}
            </option>)}
          </select>
          <button className="small" disabled={!data?.calls.length} onClick={download}>Download CSV</button>
        </span>
      }
    >
      <ErrorNote error={error} />
      {loading && !data && <Loading />}
      {data && data.totals.calls === 0 && <Empty>No AI use in this period.</Empty>}
      {data && summary && data.totals.calls > 0 && (
        <>
          <div className="kv" style={{ gridTemplateColumns: "repeat(4, minmax(0, 1fr))", marginBottom: 12 }}>
            <div>
              <span>Calls</span>
              <strong>{fmtNumber(data.totals.calls)}</strong>
            </div>
            <div>
              <span>Audio</span>
              <strong>{fmtNumber(Math.round(data.totals.audio_seconds / 60))} min</strong>
            </div>
            <div>
              <span>Cost, recorded</span>
              <strong>{usd(summary.recordedUsd, 2)}</strong>
              <span className="faint">{inr(summary.recordedUsd)}</span>
            </div>
            <div>
              <span>Cost, estimated</span>
              <strong>{usd(summary.estimatedUsd, 2)}</strong>
              <span className="faint">{inr(summary.estimatedUsd)}</span>
            </div>
            <div>
              <span>Per call</span>
              <strong>{usd(summary.perCallUsd)}</strong>
              <span className="faint">{inr(summary.perCallUsd)}</span>
            </div>
            <div>
              <span>Per minute of call</span>
              <strong>{usd(summary.perAudioMinuteUsd)}</strong>
              <span className="faint">{inr(summary.perAudioMinuteUsd)}</span>
            </div>
            <div>
              <span>Billed minutes</span>
              <strong>{fmtNumber(data.totals.billable_minutes)}</strong>
            </div>
            <label className="field" style={{ margin: 0 }}>
              <span>₹ per $</span>
              <input
                type="number"
                min={1}
                step={0.5}
                value={rupeesPerDollar}
                onChange={(e) => setRupeesPerDollar(Number(e.target.value) || 1)}
              />
            </label>
          </div>
          {summary.estimatedCalls > 0 && (
            <p className="faint">
              Gemini reported no transcription output for {fmtNumber(summary.estimatedCalls)}{" "}
              of these calls, so the estimate adds their transcripts at about 2 characters a token, $12 a million. Calls
              processed from now on keep Gemini's own counts.
            </p>
          )}

          <h3 style={{ margin: "12px 0 4px" }}>By model</h3>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Step</th>
                  <th>Model</th>
                  <th className="num">Calls</th>
                  <th className="num">Input tokens</th>
                  <th className="num">Output tokens</th>
                  <th className="num">Cost</th>
                </tr>
              </thead>
              <tbody>
                {data.by_model.map((m) => (
                  <tr key={`${m.step}${m.model}`}>
                    <td>{m.step === "transcription" ? "Transcription" : "Analysis"}</td>
                    <td className="nowrap">{m.model}</td>
                    <td className="num">{fmtNumber(m.calls)}</td>
                    <td className="num">{fmtNumber(m.input_tokens)}</td>
                    <td className="num">
                      {fmtNumber(m.output_tokens)}
                      {m.without_output > 0 && <div className="faint">{fmtNumber(m.without_output)} not reported</div>}
                    </td>
                    <td className="num">{usd(m.cost_usd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h3 style={{ margin: "12px 0 4px" }}>By company</h3>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Company</th>
                  <th className="num">Calls</th>
                  <th className="num">Audio</th>
                  <th className="num">Input tokens</th>
                  <th className="num">Output tokens</th>
                  <th className="num">Cost</th>
                  <th className="num">Per call</th>
                </tr>
              </thead>
              <tbody>
                {data.by_org.map((o) => (
                  <tr key={o.org_id}>
                    <td className="primary">{o.org_name}</td>
                    <td className="num">{fmtNumber(o.calls)}</td>
                    <td className="num nowrap">{fmtNumber(Math.round(o.audio_seconds / 60))} min</td>
                    <td className="num">{fmtNumber(o.input_tokens)}</td>
                    <td className="num">{fmtNumber(o.output_tokens)}</td>
                    <td className="num">{usd(o.cost_usd)}</td>
                    <td className="num">{usd(o.calls ? o.cost_usd / o.calls : null)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h3 style={{ margin: "12px 0 4px" }}>Calls, newest first</h3>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>When</th>
                  <th>Company</th>
                  <th className="num">Length</th>
                  <th className="num">Transcription in / out</th>
                  <th className="num">Analysis in / out</th>
                  <th className="num">Cost</th>
                </tr>
              </thead>
              <tbody>
                {data.calls.slice(0, showCalls).map((c) => (
                  <tr key={c.ref + c.processed_at}>
                    <td className="nowrap">
                      {fmtDateTime(c.processed_at, TZ)} <span className="faint">{c.ref}</span>
                    </td>
                    <td>{c.org_name}</td>
                    <td className="num">{fmtDuration(c.audio_seconds)}</td>
                    <td className="num nowrap">
                      {fmtNumber(c.transcription.input_tokens ?? 0)} /{" "}
                      {c.transcription.output_tokens ? fmtNumber(c.transcription.output_tokens) : "–"}
                    </td>
                    <td className="num nowrap">
                      {fmtNumber(c.analysis.input_tokens ?? 0)} / {fmtNumber(c.analysis.output_tokens ?? 0)}
                    </td>
                    <td className="num">{usd(c.cost_usd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {data.calls.length > showCalls && (
            <button
              className="link small"
              style={{ marginTop: 8 }}
              onClick={() => setShowCalls((n) => n + 100)}
            >
              Show more ({fmtNumber(data.calls.length - showCalls)} left)
            </button>
          )}
          <p className="faint" style={{ marginTop: 10 }}>
            Gemini's token counts at its published prices; Google's invoice is the final word. Calls show only their
            company, time and length, never what was said.
          </p>
        </>
      )}
    </Card>
  );
}

const SHORT_USE = { leads_in: "Leads", calls_out: "Calls", both: "Both", other: "Other" } as const;

/** Which integrations companies want (D-146): companies asking and what for, their paid and other seats, where they
 * are, who opened each, and the latest notes. Paid seats first; build one once enough paying companies ask. */
export function IntegrationDemandCard() {
  const { api } = useSession();
  const { data, error, loading } = useLoad(() => api.platformIntegrationDemand(), [api]);
  const name = (key: string) => INTEGRATIONS.find((i) => i.key === key)?.name ?? key;
  return (
    <Card title="Integration requests" flush>
      <ErrorNote error={error} />
      {loading && !data && (
        <div style={{ padding: 16 }}>
          <Loading />
        </div>
      )}
      {data && data.length === 0 && <Empty>No company has opened or asked for an integration yet.</Empty>}
      {data && data.length > 0 && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Integration</th>
                <th className="num">Companies asking</th>
                <th className="num">Paid seats</th>
                <th className="num">All seats</th>
                <th>For</th>
                <th className="num">Opened</th>
                <th style={{ minWidth: 240 }}>Latest note</th>
              </tr>
            </thead>
            <tbody>
              {data.map((d) => (
                <tr key={d.key}>
                  <td className="primary nowrap">{name(d.key)}</td>
                  <td className="num">
                    {fmtNumber(d.orgs_requesting)}
                    {d.paying_orgs > 0 && <span className="faint">{` · ${fmtNumber(d.paying_orgs)} paying`}</span>}
                  </td>
                  <td className="num">{fmtNumber(d.paid_seats)}</td>
                  <td className="num">{fmtNumber(d.seats)}</td>
                  <td className="nowrap">
                    {USE_CASES.filter((u) => d.use_cases[u.key] > 0)
                      .map((u) => `${SHORT_USE[u.key]} ${d.use_cases[u.key]}`)
                      .join(" · ") || <span className="faint">–</span>}
                  </td>
                  <td className="num nowrap">
                    {fmtNumber(d.opens)}
                    <span className="faint">
                      {` · ${fmtNumber(d.orgs_opened)} ${d.orgs_opened === 1 ? "company" : "companies"}`}
                    </span>
                  </td>
                  <td>
                    {d.notes[0]
                      ? (
                        <>
                          {d.notes[0].note} <span className="faint">— {d.notes[0].org}</span>
                        </>
                      )
                      : <span className="faint">–</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
